import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPEED_MAP = { very_slow: '70s', slow: '40s', medium: '25s', fast: '15s' };
const BAR_HEIGHT = 46;
const DEFAULT_BG = '#0D2137';

function cacheKey(scope) { return `op_ticker_cache_${scope}`; }
function readCache(scope) {
  try {
    const raw = sessionStorage.getItem(cacheKey(scope));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
function writeCache(scope, val) {
  try { sessionStorage.setItem(cacheKey(scope), JSON.stringify(val)); } catch { /* noop */ }
}

const VIEW_ALL = {
  all:               '/newsroom',
  education:         '/coverage/education',
  healthcare:        '/coverage/healthcare',
  'business-finance':'/coverage/business-finance',
  government:        '/coverage/government',
  'technology-ai':   '/coverage/technology-ai',
  'workforce-labor': '/coverage/workforce-labor',
};

function timeAgo(ts) {
  if (!ts) return null;
  const mins = Math.round((Date.now() - new Date(ts).getTime()) / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

function Separator({ color }) {
  return (
    <span style={{
      display: 'inline-block',
      width: 1,
      height: 14,
      background: color,
      opacity: 0.28,
      margin: '0 20px',
      flexShrink: 0,
      verticalAlign: 'middle',
    }} aria-hidden="true" />
  );
}

function TickerItem({ item, settings }) {
  const base = {
    color: settings.text_color,
    fontFamily: "'DM Sans', 'Public Sans', -apple-system, sans-serif",
    fontSize: 13,
    fontWeight: 500,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
  };
  const muted = {
    color: settings.text_color,
    opacity: 0.42,
    fontSize: 11,
    fontFamily: "'DM Sans', 'Public Sans', sans-serif",
    whiteSpace: 'nowrap',
  };

  if (item.type === 'pinned') {
    return (
      <>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          {item.tag && (
            <span style={{
              background: settings.accent_color,
              color: '#0D2137',
              fontSize: 9,
              fontWeight: 700,
              padding: '2px 7px',
              borderRadius: 6,
              letterSpacing: '0.7px',
              textTransform: 'uppercase',
              flexShrink: 0,
            }}>{item.tag}</span>
          )}
          {item.link_url
            ? <a href={item.link_url} target="_blank" rel="noopener noreferrer" style={base}>{item.text}</a>
            : <span style={base}>{item.text}</span>}
        </span>
        <Separator color={settings.accent_color} />
      </>
    );
  }

  // article
  const ago = timeAgo(item.published_at);
  return (
    <>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <Link to={`/insights/${item.url_slug}`} style={base}>{item.title}</Link>
        {ago && <span style={muted}>{ago}</span>}
      </span>
      <Separator color={settings.accent_color} />
    </>
  );
}

export default function Ticker({ scope = 'all' }) {
  // Seed initial phase from sessionStorage so route changes never pop-in/out.
  const cached = readCache(scope);
  const [phase, setPhase] = useState(cached ? (cached.enabled ? 'reserved' : 'collapsed') : 'unknown');
  const [reservedBg, setReservedBg] = useState((cached && cached.bg_color) || DEFAULT_BG);
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/ticker?scope=${scope}`)
      .then(r => {
        const d = r.data;
        const hasContent = !!(d && d.enabled && d.items && d.items.length > 0);
        if (hasContent) {
          setData(d);
          setReservedBg(d.settings.bg_color);
          setPhase('ready');
          writeCache(scope, { enabled: true, bg_color: d.settings.bg_color });
        } else {
          setPhase('collapsed');
          writeCache(scope, { enabled: false });
        }
      })
      .catch(() => setPhase('collapsed'));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  const height = (phase === 'reserved' || phase === 'ready') ? BAR_HEIGHT : 0;
  const bg = (phase === 'ready' && data) ? data.settings.bg_color : reservedBg;
  const settings = (phase === 'ready' && data) ? data.settings : null;
  const items = (phase === 'ready' && data) ? data.items : null;
  const dur = settings ? (SPEED_MAP[settings.speed] || '25s') : '25s';
  const viewAllLink = VIEW_ALL[scope] || '/newsroom';
  const wrapClass = `ticker-outer-${scope.replace(/-/g, '_')}`;

  return (
    <div
      style={{
        height,
        overflow: 'hidden',
        background: bg,
        position: 'relative',
        transition: 'height 0.25s ease',
      }}
      data-testid={`ticker-${scope}`}
    >
      {settings && items && (
        <>
          <style>{`
            @keyframes ticker-scroll-${scope.replace(/-/g, '_')} {
              from { transform: translateX(0); }
              to   { transform: translateX(-50%); }
            }
            @keyframes ticker-pulse-dot {
              0%, 100% { opacity: 1; }
              50%       { opacity: 0.3; }
            }

            .${wrapClass} .ticker-track {
              display: inline-flex;
              align-items: center;
              animation: ticker-scroll-${scope.replace(/-/g, '_')} var(--tkdur) linear infinite;
              will-change: transform;
              white-space: nowrap;
            }
            .${wrapClass}.ticker-hoverable:hover .ticker-track {
              animation-play-state: paused;
            }
            .ticker-pulse-dot {
              animation: ticker-pulse-dot 1.6s ease-in-out infinite;
            }
            @media (prefers-reduced-motion: reduce) {
              .${wrapClass} .ticker-track { animation: none; }
            }
          `}</style>

          {/* ── Full-bleed scrolling track (sits BEHIND both caps) ──────── */}
          <div
            className={`${wrapClass}${settings.pause_on_hover ? ' ticker-hoverable' : ''}`}
            style={{ position: 'absolute', inset: 0, zIndex: 1, overflow: 'hidden', display: 'flex', alignItems: 'center' }}
          >
            <div className="ticker-track" style={{ '--tkdur': dur }}>
              {[...items, ...items].map((item, i) => (
                <TickerItem key={`${scope}-${i}`} item={item} settings={settings} />
              ))}
            </div>
          </div>

          {/* ── Left cap: label — opaque + higher z-index fully occludes track ── */}
          <div style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            zIndex: 5,
            background: settings.bg_color,
            borderRight: '1px solid rgba(255,255,255,0.1)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '0 20px',
          }}>
            <span
              className="ticker-pulse-dot"
              style={{ width: 7, height: 7, borderRadius: '50%', background: settings.accent_color, flexShrink: 0 }}
              aria-hidden="true"
            />
            <span
              data-testid={`ticker-label-${scope}`}
              style={{
                fontFamily: "'DM Serif Display', Georgia, serif",
                fontSize: 13.5,
                fontWeight: 400,
                color: settings.text_color,
                letterSpacing: '0.3px',
                whiteSpace: 'nowrap',
              }}>{settings.label}</span>
          </div>

          {/* ── Right cap: View All — opaque + higher z-index fully occludes track ── */}
          <Link
            to={viewAllLink}
            style={{
              position: 'absolute',
              right: 0,
              top: 0,
              bottom: 0,
              zIndex: 5,
              background: settings.bg_color,
              borderLeft: '1px solid rgba(255,255,255,0.1)',
              color: settings.accent_color,
              fontWeight: 700,
              fontSize: 12,
              fontFamily: "'DM Sans', 'Public Sans', sans-serif",
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              padding: '0 20px',
              whiteSpace: 'nowrap',
              letterSpacing: '0.2px',
            }}
            data-testid={`ticker-view-all-${scope}`}
          >View All&nbsp;→</Link>
        </>
      )}
    </div>
  );
}
